<template>
  <section>
    <div class="section-base__title-box">
      <div class="section-base__title">
        {{ RouteText.ORGANIZATION_STATEMENTS }}
      </div>
    </div>

    <v-row>
      <v-col cols="4">
        <v-text-field
          outlined
          dense
          label="Введите номер заявления"
          append-icon="mdi-magnify"
          v-model="filter.number"
          clearable
          hide-details
          @input="textFieldInputHandler"
        />
      </v-col>

      <v-col cols="4">
        <v-select
          v-model="filter.status"
          :items="statusList"
          item-text="title"
          item-value="code"
          dense
          label="Статус"
          hide-details
          @change="changeFilter"
          multiple
          class="select"
          outlined
          height="32"
        >
          <template v-slot:prepend-item>
            <v-list-item ripple @click="toggle">
              <v-list-item-action>
                <v-icon
                  :color="filter.status.length > 0 ? 'primary darken-4' : ''"
                >
                  {{ icon }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Все</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:selection="{ item, index }">
            <v-chip class="multi" v-if="index === 0">
              <span class="text">{{ item.title }}</span>
            </v-chip>
            <span class="grey--text caption" v-if="index === 1">
              + ещё {{ filter.status.length - 1 }}
            </span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="4">
        <DateFieldCustom
          label="Выберите дату"
          :dense="true"
          :clearable="true"
          :hide-details="true"
          :value="filter.date"
          :readonly="true"
          @change="
            filter.date = $event;
            changeFilter();
          "
          :max="new Date().toISOString()"
        />
      </v-col>
    </v-row>

    <Table
      :headers="headers"
      :items="items"
      :loading="loading"
      :options="{
        itemsPerPage,
      }"
      item-key="statementId"
      disable-sort
      hide-default-footer
    >
      <template v-slot:item.statementTypeName="{ item }">
        <router-link
          :to="{
            name: RouteName.STATEMENT_DETAIL,
            params: { id: item.statementId },
          }"
          class="link"
        >
          {{ item.statementTypeName }}
        </router-link>
      </template>
      <template v-slot:item.statusName="{ item }">
        <Status
          :type-status="item.typeStatus"
          :statusPercent="item.statusPercent"
          :statusName="item.statusName"
          :statusDesc="item.statusDesc"
        />
      </template>
      <template v-slot:footer>
        <Pagination
          :total-count="total"
          :current-page="page"
          :limit-selected="itemsPerPage"
          @changeLimit="
            itemsPerPage = $event;
            changeFilter();
          "
          @changePage="
            page = $event;
            getOrganizationStatements();
          "
        />
      </template>
    </Table>
  </section>
</template>

<script>
import { RouteText } from '@/router';
import DateFieldCustom from '@/components/base/DateFieldCustom';
import Pagination from '@/components/base/Pagination.vue';
import Table from '@/components/base/Table';
import { OrganizationStatementsModule } from '@/store/modules/organizationStatements';
import Status from '@/components/elements/statement/Status/Status.vue';
import { RouteName } from '@/router';
import { getUserOrgStatementStatuses } from '@/api/statements';
import { debounce } from 'debounce';

export default {
  name: 'OrganizationStatements',
  components: {
    Pagination,
    Status,
    DateFieldCustom,
    Table,
  },
  data: () => ({
    RouteText,
    RouteName,
    filter: {
      status: [],
      number: '',
      date: '',
    },
    statusList: [],
    headers: [
      {
        text: 'Автор',
        value: 'author',
        width: '240px',
      },
      {
        text: '№ заявления',
        value: 'extId',
        width: '180px',
      },
      {
        text: 'Наименование документа',
        value: 'statementTypeName',
        width: '360px',
      },
      {
        text: 'Дата создания',
        value: 'createDate',
        width: '140px',
      },
      {
        text: 'Статус',
        value: 'statusName',
      },
    ],
    page: 1,
    itemsPerPage: 15,
    textFieldInputHandler: null,
  }),
  created() {
    getUserOrgStatementStatuses().then((list) => {
      this.statusList = list;
    });

    this.textFieldInputHandler = debounce(() => {
      this.changeFilter();
    }, 500);

    this.getOrganizationStatements();
  },
  methods: {
    getOrganizationStatements() {
      OrganizationStatementsModule.getOrganizationStatements({
        filter: this.filter,
        page: this.page,
        itemsPerPage: this.itemsPerPage,
      });
    },
    changeFilter() {
      this.page = 1;
      this.getOrganizationStatements();
    },
    toggle() {
      this.$nextTick(() => {
        if (this.isSelectedAllStatusFilter) {
          this.filter.status = [];
        } else {
          this.filter.status = this.statusList.map((item) => item.code);
        }
        this.getOrganizationStatements();
      });
    },
  },
  computed: {
    items() {
      return OrganizationStatementsModule.items;
    },
    total() {
      return OrganizationStatementsModule.total;
    },
    loading() {
      return OrganizationStatementsModule.loading;
    },
    isSelectedAllStatusFilter() {
      return this.filter.status.length === this.statusList.length;
    },
    isSelectedSomeStatusFilter() {
      return this.filter.status.length > 0;
    },
    icon() {
      if (this.isSelectedAllStatusFilter) return 'mdi-close-box';
      if (this.isSelectedSomeStatusFilter) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
  },
};
</script>

<style scoped lang="scss">
.select {
  & .more-text {
    color: $color-dark-grey;
    font-size: $font-size-middle-level-3;
  }

  &::v-deep .v-chip {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    position: relative;
    white-space: nowrap;

    &.multi {
      max-width: 70%;
    }

    span.text {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    & .caption {
      font-size: $font-size-middle-level-3 !important;
    }
  }
}
</style>
